import { Box, Chip, Tooltip } from '@mui/material';

const TABLE_CELL_FONT_SIZE = 11;
const RAW_DATA_CELL_MAX_HEIGHT = 120;

import type { GridColDef } from '@mui/x-data-grid';
import { Error as ErrorIcon, Info, Warning } from '@vy/ui-design-system';
import type { UnmeteredEventsReason, UnmeteredEventsReportItem } from '@vy/types';
import type { ReactElement } from 'react';
import { VyTableCell } from '../../../../../components/new-design/table/vy-table/VyTableCell';

const REASON_LABELS: Record<UnmeteredEventsReason, string> = {
  noMeter: 'No Meter',
  noContract: 'No Contract',
  noProductOnContract: 'No Product on Contract',
};

const REASON_COLORS: Record<UnmeteredEventsReason, 'error' | 'warning' | 'info'> = {
  noMeter: 'info',
  noContract: 'error',
  noProductOnContract: 'warning',
};

const ICON_PROPS = { width: 13, height: 13, viewBox: '0 0 24 24' };

const REASON_ICONS: Record<UnmeteredEventsReason, ReactElement> = {
  noMeter: <Info {...ICON_PROPS} />,
  noContract: <ErrorIcon {...ICON_PROPS} />,
  noProductOnContract: <Warning {...ICON_PROPS} />,
};

function buildStaticColumns(): GridColDef<UnmeteredEventsReportItem>[] {
  return [
    {
      field: 'customerAlias',
      headerName: 'Customer Alias',
      minWidth: 180,
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (_value, row) => row.customerAlias,
      renderCell: ({ row }) => <VyTableCell value={row.customerAlias} />,
    },
    {
      field: 'eventName',
      headerName: 'Name',
      minWidth: 180,
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (_value, row) => row.eventName,
      renderCell: ({ row }) => <VyTableCell value={row.eventName} />,
    },
    {
      field: 'eventCount',
      headerName: 'Event Count',
      minWidth: 120,
      flex: 1,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueGetter: (_value, row) => row.eventCount,
      renderCell: ({ row }) => <VyTableCell value={String(row.eventCount)} />,
    },
    {
      field: 'reason',
      headerName: 'Reason',
      minWidth: 180,
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (_value, row) => REASON_LABELS[row.reason],
      renderCell: ({ row }) => (
        <Chip
          icon={REASON_ICONS[row.reason]}
          label={REASON_LABELS[row.reason]}
          size="small"
          color={REASON_COLORS[row.reason]}
          variant="outlined"
          sx={{ fontSize: 11 }}
        />
      ),
    },
    {
      field: 'lastRawData',
      headerName: 'Last Event',
      minWidth: 200,
      flex: 2,
      valueGetter: (_value, row) => JSON.stringify(row.lastRawData),
      renderCell: ({ row }) => {
        const jsonText = JSON.stringify(row.lastRawData);
        const tooltipTitle = JSON.stringify(row.lastRawData, null, 2);
        return (
          <Tooltip title={tooltipTitle} placement="left">
            <Box>
              <Box
                component="pre"
                sx={{
                  fontSize: TABLE_CELL_FONT_SIZE,
                  maxHeight: RAW_DATA_CELL_MAX_HEIGHT,
                  overflow: 'auto',
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {jsonText}
              </Box>
            </Box>
          </Tooltip>
        );
      },
    },
  ];
}

function buildFilterFieldColumn(fieldName: string): GridColDef<UnmeteredEventsReportItem> {
  return {
    field: `filterFields.${fieldName}`,
    headerName: fieldName,
    minWidth: 140,
    flex: 1,
    headerAlign: 'center',
    align: 'center',
    valueGetter: (_value, row) => row.filterFields[fieldName] ?? '',
    renderCell: ({ row }) => <VyTableCell value={row.filterFields[fieldName]} />,
  };
}

export function buildUnmeteredEventsColumns(
  filterFieldNames: string[],
): GridColDef<UnmeteredEventsReportItem>[] {
  const dynamicColumns = filterFieldNames.map((fieldName) => buildFilterFieldColumn(fieldName));
  return [...buildStaticColumns(), ...dynamicColumns];
}
